/**
 * ConferenciaVerifHeader — Gradient card showing conference metadata.
 * Used by the new conferência verificação detail page.
 */
import React from 'react';
import { ConferenciaVerificacaoDetail } from '../../services/conferenciaService';
import { Building2, Users, Package, Calendar, Tag, Clock } from 'lucide-react';

interface Props {
  conferencia: ConferenciaVerificacaoDetail;
}

export const ConferenciaVerifHeader: React.FC<Props> = ({ conferencia }) => {
  return (
    <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 rounded-xl p-5 text-white shadow-lg">
      {/* Line 1: Title + Status */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <h3 className="text-xl font-bold">
            Conferência #{conferencia.numero_conferencia || '?'}
          </h3>
          <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
            conferencia.finalizada
              ? 'bg-white/20 text-white'
              : 'bg-yellow-400/90 text-yellow-900'
          }`}>
            {conferencia.finalizada ? 'Finalizada' : 'Em andamento'}
          </span>
          <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
            conferencia.tipo === 'AVULSA' ? 'bg-orange-400/90 text-orange-900' : 'bg-blue-400/20 text-white'
          }`}>
            {conferencia.tipo === 'AVULSA' ? 'Avulsa' : 'Lacre'}
          </span>
        </div>
        <div className="text-right">
          <span className="text-lg font-bold">
            {conferencia.total_conferidos}/{conferencia.total_itens}
          </span>
          <span className="text-white/70 text-sm ml-1">conferidos</span>
        </div>
      </div>

      {/* Line 2: Hospital, Cliente, OC */}
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-white/90 mb-2">
        {conferencia.hospital_nome && (
          <span className="flex items-center gap-1.5">
            <Building2 className="w-3.5 h-3.5 opacity-70" />
            {conferencia.hospital_nome}
          </span>
        )}
        {conferencia.cliente_nome && (
          <span className="flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5 opacity-70" />
            {conferencia.cliente_nome}
          </span>
        )}
        {conferencia.ordem_coleta && (
          <span className="flex items-center gap-1.5">
            <Package className="w-3.5 h-3.5 opacity-70" />
            O.C.: {conferencia.ordem_coleta}
          </span>
        )}
        {conferencia.job_id && (
          <span className="flex items-center gap-1.5">
            <Tag className="w-3.5 h-3.5 opacity-70" />
            Contagem: {conferencia.job_id}
          </span>
        )}
      </div>

      {/* Line 3: Date, Conferente */}
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-white/70">
        {conferencia.data_conferencia && (
          <span className="flex items-center gap-1.5">
            <Calendar className="w-3 h-3" />
            {conferencia.data_conferencia}
          </span>
        )}
        {conferencia.conferente_nome && (
          <span>Conferente: {conferencia.conferente_nome}</span>
        )}
        {conferencia.horario_inicio && (
          <span className="flex items-center gap-1.5">
            <Clock className="w-3 h-3" />
            Início: {conferencia.horario_inicio}
          </span>
        )}
        {conferencia.data_finalizacao && (
          <span className="flex items-center gap-1.5">
            <Clock className="w-3 h-3" />
            Fim: {conferencia.data_finalizacao}
          </span>
        )}
        {(conferencia.regime_label || conferencia.com_regime !== null) && (
          <span>
            Regime: {conferencia.regime_label
              ?? (conferencia.com_regime ? 'Sim' : 'Não')}
          </span>
        )}
      </div>
    </div>
  );
};
